/**
 * Base error class. Don't use this class directly!
 *
 * @see GenericError
 */
export class BaseError {
    className;
    message;
    timestamp;
    code;
    source;
    stack;
    reason;
    parent;
    /**
     * Important: We need public static className to be defined because we can't use constructor.name as it gets obfuscated/mangled in production builds
     *
     * @returns valid, statically defined class name of the super class
     */
    getValidClassName() {
        if ('className' in this.constructor &&
            typeof this.constructor.className === 'string' &&
            this.constructor.className) {
            return String(this.constructor.className);
        }
        if (process.env.NODE_ENV === 'development') {
            // fail in local development mode
            throw new Error(`Set a valid public static className member for error class: ${this.constructor.name}`);
        }
        // fallback to generic error in production
        return 'GenericError';
    }
    constructor({ message, code, source, parent, reason }) {
        this.className = this.getValidClassName();
        this.message = message;
        this.timestamp = new Date();
        this.code = code;
        this.source = source;
        this.reason = reason;
        this.parent =
            parent instanceof BaseError ? parent.convertToJSON(true) : parent;
        if (typeof Error.captureStackTrace === 'function') {
            // nodejs stack trace
            Error.captureStackTrace(this, this.constructor);
        }
        else {
            // browser stack trace
            this.stack = new Error().stack ?? undefined;
        }
    }
    convertToJSON(debug) {
        return {
            className: this.className,
            message: this.message,
            timestamp: this.timestamp?.toISOString(),
            code: this.code,
            source: this.source,
            stack: debug && this.stack
                ? typeof this.stack === 'string'
                    ? this.stack.split('\n').map((line) => line.trim())
                    : this.stack
                : undefined,
            reason: debug ? this.reason : undefined,
            parent: this.parent,
        };
    }
    assignJSON(json) {
        if (!json.className)
            return false;
        this.className = json.className;
        this.message = json.message ?? 'Unknown error';
        this.timestamp = json.timestamp ? new Date(json.timestamp) : new Date();
        this.code = json.code;
        this.source = json.source;
        this.stack = json.stack?.join('\n');
        this.reason = json.reason;
        this.parent = json.parent;
        return true;
    }
    static isSerializableError(json) {
        return typeof json.className === 'string' && !!json.className;
    }
    static fromJSON(json, create) {
        if (typeof json !== 'object' ||
            json === null ||
            !BaseError.isSerializableError(json)) {
            // this is not a serializable error object
            return null;
        }
        const error = create(json.className);
        if (!error) {
            // create function could not instantiate the error
            console.warn('Could not convert error from JSON due to unknown error class name:', {
                className: json.className,
                json,
            });
            return null;
        }
        if (!error.assignJSON(json)) {
            // callback says the error is invalid
            console.warn('Could not convert error from JSON due to invalid object:', {
                className: json.className,
                json,
            });
            /**
             * This is a quick fix. Unfortunately it cannot be reproduced except on preprod, which is exactly where this warning is issued
             * Problem: At this point it is clear that we have an Error Object. With SSR a zero instead of the error means that we do not catch the error on the client side and, depending on how we receive the data, it either shows the page loader or has a runtime exception.
             * However, an error page with at least the BPId is necessary. The best way to understand the previous behavior is to trigger one, like a policy that doesn't exist, and turn the logic around here
             */
            if (error.source?.location === 'SSR') {
                return error;
            }
            return null;
        }
        return error;
    }
    getStatusCode() {
        return undefined;
    }
    getParent() {
        return this.parent;
    }
    /**
     * Searches for a parent error of the given class and returns an instantiated error object of that class.
     */
    findParent(
    // we don't need to know the constructor arguments, because we are using Object.create()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    classObj) {
        let parent = this.parent;
        while (parent) {
            const hasClassName = 'className' in classObj;
            if (!hasClassName) {
                console.warn('Could not find `className` in ErrorObject:', {
                    ErrorClass: classObj,
                });
            }
            if (parent.className === (hasClassName ? classObj.className : classObj.name)) {
                const newClassObj = Object.create(classObj.prototype);
                if (newClassObj.assignJSON(parent) === false) {
                    // invalid serialized json
                    return null;
                }
                return newClassObj;
            }
            parent = parent.parent;
        }
        return null;
    }
    /**
     * Finds the first parent error that matches the given callback and returns an instantiated error object of that class.
     */
    findParentByCallback(callback, create) {
        const parents = this.getInstantiatedParents(create);
        return parents.find(callback) ?? null;
    }
    getParents() {
        const parents = [];
        let parent = this.parent;
        while (parent) {
            parents.push(parent);
            parent = parent.parent;
        }
        return parents;
    }
    getInstantiatedParents(create) {
        const errors = this.getParents().map((parent) => BaseError.fromJSON(parent, create));
        return errors.filter((error) => error);
    }
}
